/**
 * LLM Config TypeScript Types
 * 
 * Aligned with backend schemas (api/src/llm_config/schemas.py).
 * MVP providers: azure_foundry, voyage. AWS Bedrock is feature-flagged
 * (ATLAS_BEDROCK_ENABLED) and only appears in the UI when the backend
 * reports it as supported via /admin/platform-providers/supported-types.
 */

// =============================================================================
// Provider Types
// =============================================================================

export type ProviderType = 'azure_foundry' | 'voyage' | 'aws_bedrock' | 'vertex_ai';

// =============================================================================
// Provider-specific deployment_config shapes
// =============================================================================

export interface BedrockDeploymentConfig {
  /** AWS region (e.g. "us-east-1", "eu-west-1"). Required. */
  region: string;
  /** Authentication mode. */
  auth_mode: 'access_key' | 'iam_role';
  /** IAM role ARN (required when auth_mode=iam_role). */
  role_arn?: string;
  /** STS ExternalId (optional, for cross-account AssumeRole). */
  external_id?: string;
  /** Override K8s Secret name (defaults to aws-bedrock-credentials-dr). */
  secret_name?: string;
}

// =============================================================================
// Status Types — open strings, driven by API (no hardcoded enum)
// The UI uses style maps with a fallback so unknown statuses render gracefully.
// =============================================================================

/** Provider health status as reported by the API. */
export type ProviderStatus = string;

/** Model availability status as reported by the API. */
export type ModelStatus = string;

// =============================================================================
// Platform Provider Types (System-wide)
// =============================================================================

export interface PlatformProvider {
  id: string;
  provider_type: ProviderType;
  display_name: string;
  description: string | null;
  api_version: string | null;
  supports_streaming: boolean;
  supports_tools: boolean;
  supports_vision: boolean;
  supports_embeddings: boolean;
  rate_limit_rpm: number | null;
  rate_limit_tpm: number | null;
  is_active: boolean;
  // Health lifecycle
  status: string;
  last_health_check: string | null;
  last_health_error: string | null;
  consecutive_failures: number;
  /** Provider-specific configuration (e.g. BedrockDeploymentConfig). */
  deployment_config?: Record<string, unknown>;
  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface PlatformProviderEndpointResponse {
  id: string;
  endpoint: string | null;
}

export interface PlatformProviderCreate {
  provider_type: ProviderType;
  display_name: string;
  description?: string;
  endpoint?: string;
  api_version?: string;
  credential_ref: string;
  supports_streaming?: boolean;
  supports_tools?: boolean;
  supports_vision?: boolean;
  supports_embeddings?: boolean;
  rate_limit_rpm?: number;
  rate_limit_tpm?: number;
  is_active?: boolean;
  /** Provider-specific configuration (e.g. BedrockDeploymentConfig). */
  deployment_config?: Record<string, unknown>;
}

export interface PlatformProviderUpdate {
  display_name?: string;
  description?: string;
  endpoint?: string;
  api_version?: string;
  credential_ref?: string;
  supports_streaming?: boolean;
  supports_tools?: boolean;
  supports_vision?: boolean;
  supports_embeddings?: boolean;
  rate_limit_rpm?: number;
  rate_limit_tpm?: number;
  is_active?: boolean;
  /** Provider-specific configuration (e.g. BedrockDeploymentConfig). */
  deployment_config?: Record<string, unknown>;
}

// =============================================================================
// Platform Model Types (Models within Providers)
// =============================================================================

export type ModelCategory = 'main' | 'router' | 'tools' | 'embedding';

export type ModelAvailability = 'global' | 'restricted';

export interface TenantModelAccess {
  id: string;
  tenant_id: string;
  model_id: string;
  notes?: string;
  expires_at?: string;
  granted_at: string;
  granted_by: string;
}

export interface TenantModelAccessCreate {
  tenant_id: string;
  notes?: string;
}

export interface PlatformModel {
  id: string;
  provider_id: string;
  model_id: string;
  deployment_config: Record<string, unknown>;
  display_name: string;
  category: ModelCategory;
  context_window: number;
  max_output_tokens: number;
  supports_vision: boolean;
  supports_tools: boolean;
  supports_chat: boolean;
  supports_embeddings: boolean;
  supports_audio: boolean;
  supports_image_gen: boolean;
  default_temperature: number;
  default_top_p: number;
  default_max_tokens: number;
  is_active: boolean;
  is_default: boolean;
  // Health lifecycle
  model_status: string;
  last_health_check: string | null;
  last_health_error: string | null;
  // Availability
  availability: ModelAvailability;
  // Discovery metadata
  model_version: string | null;
  model_format: string | null;
  publisher: string | null;
  lifecycle_status: string | null;
  deprecation_date: string | null;
  discovered_at: string | null;
  last_discovery_sync: string | null;
  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface PlatformModelCreate {
  model_id: string;
  display_name: string;
  category?: ModelCategory;
  context_window?: number;
  max_output_tokens?: number;
  supports_vision?: boolean;
  supports_tools?: boolean;
  supports_chat?: boolean;
  supports_embeddings?: boolean;
  supports_audio?: boolean;
  supports_image_gen?: boolean;
  default_temperature?: number;
  default_top_p?: number;
  default_max_tokens?: number;
  is_active?: boolean;
  is_default?: boolean;
  deployment_config?: Record<string, unknown>;
}

// =============================================================================
// Provider Type Options (for UI select dropdowns)
// =============================================================================

export const PLATFORM_PROVIDER_TYPES = [
  { value: 'azure_foundry' as const, label: 'Azure AI Foundry', icon: '⚡', color: 'purple' },
  { value: 'voyage' as const, label: 'Voyage AI', icon: '🧭', color: 'cyan' },
  { value: 'aws_bedrock' as const, label: 'AWS Bedrock', icon: '🟠', color: 'orange' },
  { value: 'vertex_ai' as const, label: 'Google Vertex AI', icon: '🔷', color: 'blue' },
] as const;

// Provider types that require extra deployment_config fields in the admin form.
export const PROVIDERS_WITH_DEPLOYMENT_CONFIG: readonly ProviderType[] = ['aws_bedrock'] as const;

// =============================================================================
// Tenant Provider Types
// =============================================================================

export interface TenantProvider {
  id: string;
  tenant_id: string;
  name: string;
  provider_type: ProviderType;
  endpoint: string | null;
  api_version: string | null;
  supports_streaming: boolean;
  supports_tools: boolean;
  supports_vision: boolean;
  is_active: boolean;
  is_validated: boolean;
  last_validated_at: string | null;
  validation_error: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface TenantProviderCreate {
  name: string;
  provider_type: ProviderType;
  endpoint?: string;
  api_version?: string;
  api_key: string;
  supports_streaming?: boolean;
  supports_tools?: boolean;
  supports_vision?: boolean;
  validate_on_create?: boolean;
}

export interface TenantProviderUpdate {
  name?: string;
  endpoint?: string;
  api_version?: string;
  api_key?: string;
  supports_streaming?: boolean;
  supports_tools?: boolean;
  supports_vision?: boolean;
  is_active?: boolean;
}

export interface TenantProviderListResponse {
  items: TenantProvider[];
  total: number;
}

export interface ProviderValidationResult {
  is_valid: boolean;
  error?: string;
  validated_at: string;
}

// =============================================================================
// Provider Display Metadata
// =============================================================================

export interface ProviderMetadata {
  type: ProviderType;
  displayName: string;
  description: string;
  icon: string;
  iconBg: string;
  requiresEndpoint: boolean;
  requiresApiVersion: boolean;
}

export const PROVIDER_METADATA: Record<ProviderType, ProviderMetadata> = {
  azure_foundry: {
    type: 'azure_foundry',
    displayName: 'Azure AI Foundry',
    description: 'Claude and other models via Azure AI Foundry',
    icon: '⚡',
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
    requiresEndpoint: true,
    requiresApiVersion: true,
  },
  voyage: {
    type: 'voyage',
    displayName: 'Voyage AI',
    description: 'High-quality embeddings for semantic search',
    icon: '🧭',
    iconBg: 'bg-cyan-100 dark:bg-cyan-900/30',
    requiresEndpoint: false,
    requiresApiVersion: false,
  },
  aws_bedrock: {
    type: 'aws_bedrock',
    displayName: 'AWS Bedrock',
    description: 'Anthropic, Meta, Amazon and other foundation models on AWS',
    icon: '🟠',
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    requiresEndpoint: false,
    requiresApiVersion: false,
  },
  vertex_ai: {
    type: 'vertex_ai',
    displayName: 'Google Vertex AI',
    description: 'Gemini and other models via Google Cloud Vertex AI',
    icon: '🔷',
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    requiresEndpoint: false,
    requiresApiVersion: false,
  },
};

// =============================================================================
// Provider Policy Types
// =============================================================================

/** Activation mode as reported by the API. */
export type ActivationMode = string;

export interface ProviderPolicy {
  id: string;
  provider_id: string;
  allowed_lifecycle_statuses: string[];
  excluded_model_prefixes: string[];
  excluded_model_names: string[];
  required_capabilities: string[];
  allowed_categories: string[] | null;
  activation_mode: ActivationMode;
  required_sku_types: string[];
  inactive_model_prefixes: string[];
  active_models: string[];
  default_model_by_category: Record<string, string>;
  discovery_interval_seconds: number;
  created_at: string;
  updated_at: string;
}

export interface ProviderPolicyUpdate {
  allowed_lifecycle_statuses?: string[];
  excluded_model_prefixes?: string[];
  excluded_model_names?: string[];
  required_capabilities?: string[];
  allowed_categories: string[] | null;
  activation_mode?: ActivationMode;
  required_sku_types?: string[];
  inactive_model_prefixes?: string[];
  active_models?: string[];
  default_model_by_category?: Record<string, string>;
  discovery_interval_seconds?: number;
}
